import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import TextField from "../../../../Component/Forms/TextField";
import SearchableSelect from "../../../../Component/Forms/SearchableSelect";
import styles from "./styles";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const ClientUser = (props) => {
  const {
    entity,
    request,
    lastClientUser,
    clientUsers = [],
    onClientUser,
    onNewUsername,
    onNewUserEmail,
    onMessage,
    userError,
  } = props;
  const { t } = useTranslation();
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [clientUserOptions, setClientUserOptions] = useState([]);
  const [clientUser, setClientUser] = useState(null);
  const [newUsername, setNewUsername] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [useDifferentUser, setUseDifferentUser] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const [userMessageError, setUserMessageError] = useState("");
  const [clientUserError, setClientUserError] = useState("");
  const [newUsernameError, setNewUsernameError] = useState("");
  const [newUserEmailError, setNewUserEmailError] = useState("");

  useEffect(() => {
    setClientUserOptions(
      (clientUsers || []).map((u) => ({
        value: u.userEmail,
        label: `${u.username}(${u.userEmail})`,
      })),
    );
  }, [clientUsers]);

  useEffect(() => {
    const { invalidEmail, userMessageLength } = userError;

    let clientUser, userMessage;
    if (request.requestType === 1) {
      clientUser = request.clientUser;
      userMessage = request.userMessage;
    } else {
      const records = request.records || [];
      const r = records.find((r) => r.entity.id === entity.id) || {};
      if (r) {
        clientUser = r.clientUser;
        userMessage = r.userMessage;
      }
    }

    const cu = clientUser || lastClientUser;
    if (cu) {
      setClientUser({
        value: cu.userEmail,
        label: `${cu.username}(${cu.userEmail})`,
      });
      onClientUser(cu);
    }
    if (userMessage) {
      setUserMessage(userMessage);
      onMessage(userMessage);
    }
  }, [request, entity]);

  useEffect(() => {
    setClientUserError("");
    setNewUsernameError("");
    setNewUserEmailError("");
    if (userError) {
      const { invalidEmail, userMessageLength } = userError;
      if (invalidEmail) {
        setNewUserEmailError(
          t("messages.record.documentRequest.clientUserEmailInvalid"),
        );
      } else if (userMessageLength) {
        setUserMessageError(
          t("messages.record.documentRequest.userMessageError"),
        );
      } else if (clientUsers.length > 0 && !useDifferentUser && !clientUser) {
        setClientUserError(
          t("messages.record.documentRequest.clientUserError"),
        );
      } else {
        if (!newUsername) {
          setNewUsernameError(
            t("messages.record.documentRequest.clientUserNameError"),
          );
        }
        if (!newUserEmail) {
          setNewUserEmailError(
            t("messages.record.documentRequest.clientUserEmailError"),
          );
        }
      }
    }
  }, [userError, useDifferentUser]);

  const handleClientUserChange = (u) => {
    setClientUser(u);
    onClientUser(clientUsers.find((cu) => cu.userEmail === u.value));
  };

  const handleNewUsernameChange = (username) => {
    setNewUsername(username);
    onNewUsername(username);
  };

  const handleNewUserEmailChange = (username) => {
    setNewUserEmail(username);
    onNewUserEmail(username);
  };

  const handleMessageChange = (message) => {
    setUserMessage(message);
    onMessage(message);
  };

  const handleUseDifferentChange = (e) => {
    if (e.target.checked) {
      setClientUser(null);
      onClientUser(null);
    } else {
      setNewUsername("");
      onNewUsername("");
      setNewUserEmail("");
      onNewUserEmail("");
    }
    setUseDifferentUser(e.target.checked);
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
  ];

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography>
          {t("labels.record.documentRequest.form.clientUser.header")}​
        </Typography>
      </Grid>
      {clientUsers.length > 0 && (
        <Grid item xs={6}>
          <SearchableSelect
            label={t(
              "labels.record.documentRequest.form.clientUser.clientUser",
            )}
            options={clientUserOptions}
            value={clientUser}
            changeHandler={handleClientUserChange}
            disableClearable={true}
            required={true}
            disabled={useDifferentUser}
          />
          <div className={classes.errorText}>{clientUserError}</div>
        </Grid>
      )}
      {clientUsers.length > 0 && (
        <Grid item xs={4}>
          <FormControlLabel
            control={
              <Switch
                checked={useDifferentUser}
                disableRipple
                color="primary"
                classes={{
                  switchBase: classes.switchBase,
                  thumb: classes.thumb,
                  track: classes.track,
                  checked: classes.checked,
                }}
                onChange={handleUseDifferentChange}
              />
            }
            label={t(
              "labels.record.documentRequest.form.clientUser.useDifferentUser",
            )}
            labelPlacement="top"
          />
        </Grid>
      )}
      {(useDifferentUser || clientUsers.length === 0) && (
        <Grid item xs={6}>
          <TextField
            label={t("labels.record.documentRequest.form.clientUser.username")}
            type="text"
            placeholder={t(
              "labels.record.documentRequest.form.clientUser.username",
            )}
            value={newUsername}
            onChange={handleNewUsernameChange}
            required={true}
          />
          <div className={classes.errorText}>{newUsernameError}</div>
        </Grid>
      )}
      {(useDifferentUser || clientUsers.length === 0) && (
        <Grid item xs={6}>
          <TextField
            label={t("labels.record.documentRequest.form.clientUser.userEmail")}
            type="text"
            placeholder={t(
              "labels.record.documentRequest.form.clientUser.userEmail",
            )}
            value={newUserEmail}
            onChange={handleNewUserEmailChange}
            required={true}
          />
          <div className={classes.errorText}>{newUserEmailError}</div>
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography>
          {t("labels.record.documentRequest.form.clientUser.userMessage")}
        </Typography>
      </Grid>
      <Grid item xs={12} style={{ minHeight: 140 }}>
        <ReactQuill
          theme="snow"
          value={userMessage}
          onChange={handleMessageChange}
          modules={modules}
          formats={formats}
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        />
      </Grid>
    </Grid>
  );
};

export default withStyles(styles, { withTheme: true })(ClientUser);
